import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ROUTES } from 'app/shared/consts/paths';
import { useErrors } from 'app/shared/contexts/errors';
import { ErrorRouteState } from 'app/shared/types/errors.types';

/**
 * Watches the `genericErrors` channel of `ErrorsContext` and routes the user
 * to `/error` as soon as a terminal error appears. Renders nothing.
 *
 * The page-level <ErrorPage> reads `genericErrors` from the same context, so
 * by the time the user arrives at `/error` the message is already there. The
 * back button on that page returns the user to the previous route once the
 * underlying issue is gone (e.g. retry succeeded, hideGenericErrors fired).
 *
 * Replaces the old early-return in `AppContentWrapper` — that flow rendered
 * `<ErrorPage />` in place of the current route without changing the URL,
 * which broke the back button and made reloads land back on the same
 * (broken) route instead of `/error`.
 */
export const ErrorNavigator = (): null => {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const { genericErrors, hideErrors } = useErrors();

  useEffect(() => {
    if (genericErrors.length === 0) return;
    if (pathname === ROUTES.ERROR) return;
    // The terminal error supersedes any queued recoverable ones: they usually
    // come from the same failure, and would otherwise pop up as a modal the
    // moment the user leaves /error.
    hideErrors();
    // `replace` so the back button on /error doesn't return to the route
    // that just errored — that would re-fire the same query, re-add the
    // same generic error and loop straight back to /error. Okay closes the
    // miniapp rather than going up: the slots list shares `useEventQuery`
    // with the slot detail, so returning there would bounce straight back
    // (SMB-1287). The route is kept in navigation state instead, so the
    // session-expired screen can return to it once the token is fresh.
    const state: ErrorRouteState = { from: { pathname, search, hash } };
    navigate(ROUTES.ERROR, { replace: true, state });
  }, [genericErrors.length, pathname, search, hash, navigate, hideErrors]);

  return null;
};
